import React from 'react';
import { Box, Container } from '@mui/material';

/**
 * The width every page's content sits in.
 *
 * Exported because three screens — the home page, sign-in and sign-up — lay
 * themselves out rather than going through `PageLayout`, and the nine pages
 * that each capped their own column at 860px were the reason no two screens
 * lined up.
 */
export const CONTENT_MAX_WIDTH = 'lg';

/**
 * The frame every page sits in.
 *
 * It used to paint a lavender gradient of its own and force itself to
 * `100vh - 70px` tall. Both were wrong: the gradient was the boilerplate's,
 * and the 70px was the height of a toolbar that no longer exists — with a
 * header, a footer and a bar at the bottom of a phone, that arithmetic made
 * every page a little taller than the window and put a scrollbar on screens
 * that had nothing to scroll. The shell owns the background and the height
 * now; this owns the column.
 *
 * `centered` is not a page style, it is the "one small thing on its own"
 * case — a spinner, a "listing not found" card. A page with content always
 * passes `centered={false}`.
 */
function PageLayout({ children, centered = true, maxWidth = CONTENT_MAX_WIDTH }) {
  return (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', width: '100%' }}>
      <Container maxWidth={maxWidth} sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: centered ? 'center' : 'flex-start',
            alignItems: centered ? 'center' : 'stretch',
            // Enough to read as vertically centred without making the page
            // taller than the window.
            minHeight: centered ? '60vh' : 0,
            py: { xs: 3, md: 4 },
          }}
        >
          {children}
        </Box>
      </Container>
    </Box>
  );
}

export default PageLayout;
